import { useHistory, useParams, withRouter } from "react-router-dom/cjs/react-router-dom.min";
import { IconCard, LayoutSingleColumn, NamedLink, Page } from "../../components";
import FooterComponent from "../FooterContainer/FooterContainer";
import TopbarContainer from "../TopbarContainer/TopbarContainer";
import css from "./PostPage.module.css"
import { manageDisableScrolling, isScrollingDisabled } from "../../ducks/ui.duck";
import { connect } from "react-redux";
import { compose } from "redux";
import { injectIntl } from "react-intl";
import { useEffect, useState } from "react";
import { getBlogByIdAdmin, getBlogByIdUser } from "../../util/api";
import moment from "moment";
import { BlogDetailSkeleton } from "../../components/BlogSkeleton/BlogPageSkeleton";
import DOMPurify from "dompurify";
import { useConfiguration } from "../../context/configurationContext";

export const PostPageComponent = props => {
    const [data, setData] = useState(null)
    const { id, slug } = useParams();
    const history = useHistory()
    const config = useConfiguration()
    const {
        scrollingDisabled,
        currentUser,
        blogData
    } = props;

    const isAdmin = (currentUser && currentUser?.id && currentUser?.attributes?.profile?.privateData?.admin);

    // Use Redux initially for SSR properly, fetch client-side if missing or isAdmin
    useEffect(() => {
        const fetchBlog = async () => {
            if (isAdmin) {
                const res = await getBlogByIdAdmin({ id });
                setData(res?.data);
            } else if (!blogData) {
                // Not fetched yet
                const res = await getBlogByIdUser({ slug });
                setData(res?.data);
                if (res?.data?.slug && slug !== res?.data?.slug) {
                    history.replace(`/everything-fractional/${id}/${res?.data?.slug}`);
                }
            } else {
                setData(blogData);
                if (blogData?.slug && slug !== blogData?.slug) {
                    history.replace(`/everything-fractional/${id}/${blogData?.slug}`);
                }
            }
        };

        fetchBlog();
    }, [id, slug, isAdmin, blogData, history]);

    const cleanHTML = (content) => DOMPurify.sanitize(content);

    // SEO: Create Article structured data
    const createArticleSchema = () => {
        if (!data) return null;
        
        return {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: data.title,
            description: data.description,
            image: data.image ? [data.image] : [],
            datePublished: data.publishedAt,
            dateModified: data.updatedAt || data.publishedAt,
            author: {
                '@type': 'Person',
                name: 'Raman',
                jobTitle: 'Founder of Forteh'
            },
            publisher: {
                '@type': 'Organization',
                name: config.marketplaceName,
                logo: {
                    '@type': 'ImageObject',
                    url: config.branding.logoImageMobileURL
                }
            },
            mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `${config.marketplaceRootURL}/everything-fractional/${id}/${data.slug}`
            },
            url: `${config.marketplaceRootURL}/everything-fractional/${id}/${data.slug}`,
            wordCount: data.content ? data.content.replace(/<[^>]*>/g, '').split(' ').length : 0,
            timeRequired: data.readTime ? `PT${data.readTime.replace(/[^0-9]/g, '')}M` : 'PT5M'
        };
    };

    // SEO: Create breadcrumb schema
    const createBreadcrumbSchema = () => {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: config.marketplaceRootURL
                },
                {
                    '@type': 'ListItem',
                    position: 2,
                    name: 'Blog',
                    item: `${config.marketplaceRootURL}/everything-fractional`
                },
                {
                    '@type': 'ListItem',
                    position: 3,
                    name: data?.title || 'Blog Post',
                    item: `${config.marketplaceRootURL}/everything-fractional/${id}/${data?.slug}`
                }
            ]
        };
    };

    // SEO: Prepare social media images
    const getSocialImages = () => {
        if (data?.image) {
            return [
                {
                    width: 1200,
                    height: 630,
                    url: data.image
                }
            ];
        }
        return [
            {
                width: 1200,
                height: 630,
                url: config.branding.facebookImage
            }
        ];
    };

    const getTwitterImages = () => {
        if (data?.image) {
            return [
                {
                    width: 600,
                    height: 314,
                    url: data.image
                }
            ];
        }
        return [
            {
                width: 600,
                height: 314,
                url: config.branding.twitterImage
            }
        ];
    };

    const schema = data ? [createArticleSchema(), createBreadcrumbSchema()] : [];

    return (
        <Page 
            title={data ? `${data.title} - Forteh Blog` : "Loading... - Forteh Blog"} 
            description={data?.description || "Read the latest insights on fractional work and careers from Forteh."}
            scrollingDisabled={scrollingDisabled}
            schema={schema}
            openGraphType="article"
            published={data?.publishedAt}
            updated={data?.updatedAt}
            facebookImages={getSocialImages()}
            twitterImages={getTwitterImages()}
            author="Raman - Founder of Forteh"
            socialSharing={{
                title: data ? `${data.title} - Forteh Blog` : "Loading... - Forteh Blog",
                description: data?.description || "Read the latest insights on fractional work and careers from Forteh.",
                images1200: getSocialImages(),
                images600: getTwitterImages()
            }}
        >
            <LayoutSingleColumn
                topbar={
                    <TopbarContainer
                        className={css.topbar}
                        mobileRootClassName={css.mobileTopbar}
                        desktopClassName={css.desktopTopbar}
                        currentPage="PostPage"
                    />
                }
                footer={<FooterComponent />}
                className={css.pageRoot}
            >
                <div className={css.outerWrapper}>
                    <div className={css.mainWrapper}>
                        <div className={css.container}>
                            {data ? (
                                <>
                                    <div className={css.editIcon}>
                                        {isAdmin && (
                                            <NamedLink
                                                name="AdminBlogPage"
                                                to={{
                                                    pathname: "/adminBlog",
                                                    search: `?id=${data._id}`,
                                                    state: { blog: data }
                                                }}
                                            >
                                                <IconCard brand="edit" /> Edit
                                            </NamedLink>
                                        )}
                                    </div>

                                    <h4 className={css.title}>{data?.title}</h4>
                                    <h4 className={css.description}>{data?.description}</h4>
                                    <h4 className={css.dateWrapper}>
                                        {moment(data?.publishedAt).format("DD MMM YYYY")}
                                    </h4>

                                    <h4 className={css.readTime}>Read Time : {data?.readTime}</h4>

                                    {data?.image && (
                                        <div className={css.img}>
                                            <img src={data?.image} alt={data?.title} />
                                        </div>
                                    )}

                                    <div className={css.nameText}>Raman</div>
                                    <div className={css.designation}>Founder of Forteh</div>
                                    <div
                                        className={css.content}
                                        dangerouslySetInnerHTML={{
                                            __html: cleanHTML(data?.content),
                                        }}
                                    />

                                </>
                            ) : <BlogDetailSkeleton count={6} />
                            }
                        </div>

                    </div>
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};



const mapStateToProps = state => {
    const { currentUser } = state.user;
    const { blog } = state.PostPage || {};
    return {
        currentUser,
        blogData: blog,
        scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    onManageDisableScrolling: (componentId, disableScrolling) =>
        dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const PostPage = compose(
    withRouter,
    connect(
        mapStateToProps,
        mapDispatchToProps
    ),
    injectIntl
)(PostPageComponent);

export default PostPage;